import React from 'react';
import { RotateCcw, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Update state so the next render will show the fallback UI
    this.setState(prevState => ({
      hasError: true,
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Log the error to console in development
    console.error('Error caught by ErrorBoundary:', error);
    console.error('Error Info:', errorInfo);

    // In production, you could send error to a monitoring service
    // Example: logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    // Clear the error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Optionally reload the page
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className={cn(
          'min-h-screen w-full flex items-center justify-center',
          'bg-gradient-to-br from-stone-50 to-stone-100',
          'dark:from-zinc-950 dark:to-zinc-900',
          'p-4'
        )}>
          <div className="max-w-md w-full">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-red-500/20 rounded-full blur-xl"></div>
                <div className={cn(
                  'relative w-16 h-16 rounded-full',
                  'bg-red-100 dark:bg-red-900/30',
                  'flex items-center justify-center'
                )}>
                  <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
                </div>
              </div>
            </div>

            {/* Error Title */}
            <h1 className={cn(
              'text-2xl font-bold text-center mb-2',
              'text-stone-900 dark:text-white'
            )}>
              Something went wrong
            </h1>

            {/* Error Description */}
            <p className={cn(
              'text-center text-sm mb-6',
              'text-stone-600 dark:text-zinc-400'
            )}>
              We encountered an unexpected error. Try reloading the page, or contact support if the problem persists.
            </p>

            {/* Error Details (Development only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className={cn(
                'mb-6 p-4 rounded-lg border',
                'bg-red-50 dark:bg-red-900/10',
                'border-red-200 dark:border-red-900/30',
                'overflow-auto max-h-40'
              )}>
                <p className="text-xs font-mono text-red-600 dark:text-red-400 whitespace-pre-wrap break-words">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            {/* Reload Button */}
            <button
              onClick={this.handleReload}
              className={cn(
                'w-full py-3 px-4 rounded-lg font-medium',
                'bg-gradient-to-r from-gathrr-500 to-gathrr-600',
                'hover:from-gathrr-600 hover:to-gathrr-700',
                'text-white transition-all duration-200',
                'flex items-center justify-center gap-2',
                'focus:outline-none focus:ring-2 focus:ring-gathrr-500/50'
              )}
            >
              <RotateCcw className="w-4 h-4" />
              Reload Page
            </button>

            {/* Footer Text */}
            <p className={cn(
              'text-center text-xs mt-6',
              'text-stone-400 dark:text-zinc-500'
            )}>
              Error #{this.state.errorCount}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
